import { createAxiosClient, ServiceProxyMap } from '@atlassian/temp-client-utils';
import { BillingDetails, CloudSubscriptionBillEstimate } from '@atlassian/cssxp-types-billing';
import { ContactSearchResults, FetchEntitlementContactParams } from '@atlassian/cssxp-types-contact';
import {
  AssignReservedCloudNameRequest,
  AssignReservedCloudNameResponse,
  CloudJobResponse,
  cloudSiteNameSearchResponse,
  CloudSiteRenameAvailabilityResult,
  CloudSiteRenameEligibility,
  CloudSiteResults,
  CloudSubscription,
  CloudSubscriptionSearch,
  CloudTrialExtensionEligibility,
  CloudTrialExtensionResult,
  DestroyEntitlementRequest,
  RemovableProduct,
  ReservedCloudSiteSearchRequest,
  ReservedCloudSiteSearchResponse,
  ReserveNewCloudSiteRequest,
  ReserveNewCloudSiteResponse,
  Results,
  TransferSiteURLRequest,
  URLRenameRequest,
} from '@atlassian/cssxp-typescommon';
import moment from 'moment';

const client = createAxiosClient({ baseURL: '/api/1/cloud-licenses' }, ServiceProxyMap.ANS);

// TODO: Remove searchOptions since it is only required by the SearchRequestFactory.
// This proxy method is being used by both site-admins and users but will eventually
// be combined. Since they both work off different patterns this method must support
// search options and params being combined and also split.

export async function fetchBillingDetails(cloudSiteId: string): Promise<BillingDetails> {
  const response = await client.get(`/${cloudSiteId}/billing-details`);

  return response.data;
}

export async function fetchBillEstimate(cloudSiteId: string): Promise<CloudSubscriptionBillEstimate> {
  const response = await client.get(`/${cloudSiteId}/bill-estimate`);

  return response.data;
}

export const assignReservedCloudSiteName = async (
  requestPayload: AssignReservedCloudNameRequest
): Promise<AssignReservedCloudNameResponse> => {
  const response = await client.post('/assignReservedCloudSiteName', requestPayload);
  return response.data;
};

export async function fetchContacts(params: FetchEntitlementContactParams): Promise<ContactSearchResults> {
  const { entitlementKey, ...searchOptions } = params;

  const response = await client.get(`/${entitlementKey}/contacts`, {
    params: searchOptions,
  });

  return response.data;
}

export async function getCloudSiteRenameEligibility(cloudSiteId: string) {
  const response = await client.get<CloudSiteRenameEligibility>(`${cloudSiteId}/can-rename`);

  return response.data;
}

export async function isNameAvailableForRename(cloudSiteId: string, newCloudSiteName: string) {
  const response = await client.get<CloudSiteRenameAvailabilityResult>(`${cloudSiteId}/name-availability`, {
    params: { newCloudSiteName },
  });

  return response.data;
}

export async function renameCloudSite(requestPayload: URLRenameRequest): Promise<string> {
  const { cloudSiteId, newCloudSiteName } = requestPayload;
  const response = await client.post<{ requestId: string }>(`${cloudSiteId}/rename`, {
    newCloudSiteName,
  });
  return response.data.requestId;
}

export async function reactivateCloudSite(cloudSiteId: string) {
  const response = await client.post<{ requestId: string }>(`/${cloudSiteId}/reactivations`);

  return response.data.requestId;
}

export async function deactivateCloudSite(cloudSiteId: string) {
  const response = await client.post<{ requestId: string }>(`/${cloudSiteId}/deactivations`);

  return response.data.requestId;
}

export async function extendExtendedCloudTrial(
  cloudSiteId: string,
  ccpEntitlementId: string | undefined,
  sen: string,
  trialExtendMonth: number
) {
  const response = await client.post<void>(`${cloudSiteId}/extended-trial/extend`, {
    sen,
    ccpEntitlementId,
    trialExtendMonth,
  });
  return response.data;
}

export async function changeUserTier(cloudSiteId: string, ccpEntitlementId: string, sen: string, usageLimit: number) {
  const response = await client.post<void>(`${cloudSiteId}/extended-trial/change-usage-limit`, {
    sen,
    ccpEntitlementId,
    usageLimit,
  });

  return response.data;
}

export async function deleteCloudSite(cloudSiteId: string) {
  const response = await client.post<{ requestId: string }>(`/${cloudSiteId}/deletions`);
  return response.data.requestId;
}

export async function destroyEntitlement(requestPayload: DestroyEntitlementRequest) {
  const response = await client.post<{ requestId: string }>(`/destroyEntitlement`, requestPayload);
  const requestId = response.data.requestId;
  return await pollUntilJobCompleted(requestPayload.cloudSiteId, requestId, 'REMOVE');
}

export async function fetchRemovableProducts(cloudSiteId: string): Promise<RemovableProduct[]> {
  const response = await client.get(`/${cloudSiteId}/removable-products`);

  return response.data;
}

// eslint-disable-next-line max-params
export async function pollUntilJobCompleted(
  cloudSiteId: string,
  jobRequestId: string,
  action: string,
  interval = 1000,
  pollingTimeout = 180000
) {
  const startTime = moment();

  const poller = async (resolve: (job: CloudJobResponse) => void, reject: (error: Error) => void) => {
    try {
      const jobResponse = await fetchJobStatus(cloudSiteId, jobRequestId, action);

      if (jobResponse.completed) {
        return resolve(jobResponse);
      } else if (moment().diff(startTime, 'ms') < pollingTimeout) {
        setTimeout(poller, interval, resolve, reject);
      } else {
        reject(new Error('Polling timeout - COFS is still processing job, you may check back later'));
      }
    } catch (error: any) {
      reject(error);
    }
  };

  return new Promise<CloudJobResponse>(poller);
}

export async function removeCloudProducts(cloudSiteId: string, productKeys: string[]): Promise<string> {
  const response = await client.post<{ requestId: string }>(`/${cloudSiteId}/remove-products`, {
    products: productKeys.map((productKey) => ({ productKey })),
  });

  return response.data.requestId;
}

export async function fetchJobStatus(cloudSiteId: string, jobRequestId: string, action: string) {
  const response = await client.get<CloudJobResponse>(
    `/${encodeURIComponent(cloudSiteId)}/jobs/${encodeURIComponent(jobRequestId)}/${encodeURIComponent(action)}
      `
  );

  return response.data;
}

export async function impersonateSiteAdmin(cloudSiteId: string) {
  const response = await client.post(`/${cloudSiteId}/site-admin`);

  return response.data;
}

export async function fetchCloudSite(senOrCloudSiteId: string): Promise<CloudSubscription> {
  const response = await client.get(`/${senOrCloudSiteId}`);

  return response.data;
}

export async function isEligibleForTrialExtension(cloudSiteId: string) {
  const response = await client.get<CloudTrialExtensionEligibility>(`${cloudSiteId}/can-extend`);
  return response.data;
}

export async function extendTrial(cloudSiteId: string, trialEndDate: string) {
  const response = await client.post<CloudTrialExtensionResult>(`${cloudSiteId}/extend-trial`, { trialEndDate });
  return response.data;
}

export async function convertExtendedCloudSiteToPaid(
  cloudSiteId: string,
  sen: string,
  ccpEntitlementId: string | undefined
) {
  await client.delete(`${cloudSiteId}/extended-trial/${sen}/${ccpEntitlementId}`);
}
export interface LinkedOrg {
  linkedOrgId?: string | null;
  linkedOrgName?: string | null;
  isScimEnabled?: boolean | null;
}

export async function fetchLinkedOrg(cloudSiteId: string) {
  const response = await client.get<LinkedOrg>(`${cloudSiteId}/linked-org`);

  return response.data;
}

export const reservedCloudSiteSearchWithName = async (
  requestPayload: ReservedCloudSiteSearchRequest
): Promise<cloudSiteNameSearchResponse> => {
  try {
    const response = await client.post('/reservedCloudSiteSearchWithName', requestPayload);
    return response.data;
  } catch (e: any) {
    const errorMessage = e?.response?.data?.message ?? e.message;
    throw new Error(errorMessage);
  }
};

export const reservedCloudSiteSearch = async (
  requestPayload: ReservedCloudSiteSearchRequest
): Promise<ReservedCloudSiteSearchResponse> => {
  const response = await client.post('/reservedCloudSiteSearch', requestPayload);
  return response.data;
};

export const reserveNewCloudSiteName = async (
  requestPayload: ReserveNewCloudSiteRequest
): Promise<ReserveNewCloudSiteResponse> => {
  const response = await client.post('/reserveNewCloudName', requestPayload);
  return response.data;
};
export const updateReservedCloudSiteName = async (
  requestPayload: ReserveNewCloudSiteRequest
): Promise<ReserveNewCloudSiteResponse> => {
  const response = await client.put('/updateReservedCloudSiteName', requestPayload);
  return response.data;
};

interface UnAssignReservedCloudNameRequest extends AssignReservedCloudNameRequest {
  ARI: string;
  cloudSiteId: string;
}

export const unAssignReservedCloudSiteName = async (
  requestPayload: UnAssignReservedCloudNameRequest
): Promise<AssignReservedCloudNameResponse> => {
  const response = await client.post('/unAssignReservedCloudSiteName', requestPayload);
  return response.data;
};

export async function getCloudSiteHostNameDetails(cloudSiteId: string) {
  const response = await client.get<CloudSiteResults>(`/getCloudSiteHostNameDetail/${cloudSiteId}`);
  return response.data;
}

export async function transferSiteUrl(payload: TransferSiteURLRequest) {
  const { fromEntityId, toEntityId, hostname } = payload;
  const response = await client.post(`${fromEntityId}/siteTransfer/${toEntityId}/${hostname}`);
  return response.data;
}

export async function search(searchPayload: CloudSubscriptionSearch): Promise<Results<CloudSubscription>> {
  const response = await client.get('/', {
    params: searchPayload,
  });

  return response.data;
}
